import {
  ExclamationCircleOutlined,
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined
} from "@ant-design/icons"
import { Button, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

const { Text } = Typography

export type ListEmptyObject =
  | "projects"
  | "leads"
  | "customers"
  | "subcontractors"
  | "suppliers"
  | "teamMembers"

export type ListEmptyVariant = "error" | "search" | "filtered" | "none"

/**
 * Why a list is empty, in the order that matters: a failed load hides
 * everything else, so it wins even when a search or filter is also set.
 */
export function listEmptyVariant({
  loadFailed,
  hasSearch,
  activeFilterCount = 0
}: {
  loadFailed?: boolean
  hasSearch?: boolean
  activeFilterCount?: number
}): ListEmptyVariant {
  if (loadFailed) return "error"
  if (hasSearch) return "search"
  if (activeFilterCount > 0) return "filtered"
  return "none"
}

interface ListEmptyStateProps {
  object: ListEmptyObject
  variant: ListEmptyVariant
  onRetry?: () => void
  onClearFilters?: () => void
  /** The create action shown on a first-use empty list, when the user may create. */
  createAction?: React.ReactNode
}

const ICONS: Record<ListEmptyVariant, React.ReactNode> = {
  error: <ExclamationCircleOutlined />,
  search: <SearchOutlined />,
  filtered: <SearchOutlined />,
  none: <InboxOutlined />
}

/**
 * The one empty state every list shares, so that "we couldn't load" never
 * looks like "you have nothing" — the second reads as deleted data.
 */
export function ListEmptyState({
  object,
  variant,
  onRetry,
  onClearFilters,
  createAction
}: ListEmptyStateProps) {
  const { t } = useTranslation()
  const isError = variant === "error"

  const hint =
    variant === "error"
      ? t("listEmptyState.errorHint")
      : variant === "filtered"
        ? t("listEmptyState.filteredHint")
        : variant === "search"
          ? t("listEmptyState.searchHint")
          : null

  return (
    <div
      role={isError ? "alert" : "status"}
      data-testid={`list-empty-${variant}`}
      className="flex min-h-[200px] flex-col items-center justify-center gap-2 px-6 py-6 text-center"
    >
      <div
        aria-hidden
        className={`flex size-12 items-center justify-center rounded-full text-xl ${
          isError ? "bg-error-bg text-error" : "bg-primary-bg text-primary"
        }`}
      >
        {ICONS[variant]}
      </div>
      <Text strong>{t(`listEmptyState.${object}.${variant}`)}</Text>
      {hint && (
        <Text type="secondary" className="max-w-sm">
          {hint}
        </Text>
      )}
      {isError && onRetry && (
        <Button icon={<ReloadOutlined />} onClick={onRetry}>
          {t("listEmptyState.retry")}
        </Button>
      )}
      {variant === "filtered" && onClearFilters && (
        <Button onClick={onClearFilters}>
          {t("listEmptyState.clearFilters")}
        </Button>
      )}
      {variant === "none" && createAction}
    </div>
  )
}
